import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Progress, Alert, Button, Card, Space, Tag, message } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, DownloadOutlined, LoadingOutlined } from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
    GET_PORTAL_BILL_EXPORT_DOWNLOAD_URL,
    GET_PORTAL_BILL_EXPORT_PROGRESS,
} from 'graphql/queries/bills';
import { PortalBillExportProgress } from 'graphql/types';

type Props = {
    open: boolean;
    taskIds: string[];
    onClose: () => void;
    onCompleted?: () => void;
};

const BillExportProgressModal: React.FC<Props> = ({ open, taskIds, onClose, onCompleted }) => {
    const { t } = useTranslation();
    const [progressMap, setProgressMap] = useState<Record<string, PortalBillExportProgress>>({});
    const [fetchProgress] = useLazyQuery<{ portalBillExportProgress: PortalBillExportProgress }>(
        GET_PORTAL_BILL_EXPORT_PROGRESS,
        { fetchPolicy: 'no-cache' },
    );
    const [fetchDownloadUrl] = useLazyQuery<{ portalBillExportDownloadUrl: string }>(
        GET_PORTAL_BILL_EXPORT_DOWNLOAD_URL,
        { fetchPolicy: 'no-cache' },
    );

    const pollProgress = useCallback(async () => {
        const next: Record<string, PortalBillExportProgress> = {};
        for (const taskId of taskIds) {
            const { data } = await fetchProgress({ variables: { taskId } });
            if (data?.portalBillExportProgress) {
                next[taskId] = data.portalBillExportProgress;
            }
        }
        setProgressMap(next);
    }, [taskIds, fetchProgress]);

    useEffect(() => {
        if (!open || taskIds.length === 0) {
            return undefined;
        }
        setProgressMap({});
        pollProgress();
        const timer = window.setInterval(pollProgress, 5000);
        return () => window.clearInterval(timer);
    }, [open, taskIds, pollProgress]);

    const progresses = useMemo(
        () => taskIds.map((id) => progressMap[id]).filter(Boolean) as PortalBillExportProgress[],
        [taskIds, progressMap],
    );

    const allCompleted =
        progresses.length === taskIds.length &&
        progresses.every((p) => p.status === 'SUCCESS' || p.status === 'FAILED');

    const totalProgress =
        progresses.length > 0
            ? Math.round(progresses.reduce((sum, p) => sum + p.progress, 0) / progresses.length)
            : 0;

    const totalStatus = useMemo(() => {
        if (progresses.length === 0) return 'PENDING';
        if (progresses.some((p) => p.status === 'FAILED')) return 'FAILED';
        if (progresses.every((p) => p.status === 'SUCCESS')) return 'SUCCESS';
        return 'RUNNING';
    }, [progresses]);

    useEffect(() => {
        if (open && allCompleted) {
            onCompleted?.();
        }
    }, [open, allCompleted, onCompleted]);

    const getFileTypeLabel = (fileType: number) => {
        switch (fileType) {
            case 1:
                return t('billExport.fileTypeSummary');
            case 2:
                return t('billExport.fileTypeDetail');
            default:
                return t('billExport.fileTypeUnknown');
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'SUCCESS':
                return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
            case 'FAILED':
                return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
            case 'RUNNING':
            case 'PENDING':
                return <LoadingOutlined style={{ color: '#1890ff' }} />;
            default:
                return null;
        }
    };

    const getTaskStatusText = (task: PortalBillExportProgress) => {
        switch (task.status) {
            case 'PENDING':
                return t('billExport.pending');
            case 'RUNNING':
                return task.currentStep || t('billExport.running');
            case 'SUCCESS':
                return task.fileName || t('billExport.completed');
            case 'FAILED':
                return t('billExport.failed');
            default:
                return t('billExport.waiting');
        }
    };

    const handleDownload = async (taskId: string) => {
        const { data } = await fetchDownloadUrl({ variables: { taskId } });
        const url = data?.portalBillExportDownloadUrl;
        if (!url) {
            message.error(t('billExport.downloadFailed'));
            return;
        }
        window.open(url, '_blank');
    };

    return (
        <Modal
            open={open}
            title={t('billExport.titleWithCount', { count: taskIds.length })}
            onCancel={onClose}
            footer={
                <Button onClick={onClose}>
                    {totalStatus === 'RUNNING' ? t('billExport.backgroundRun') : t('billExport.close')}
                </Button>
            }
            width={700}
            maskClosable={false}
        >
            <div className="space-y-4">
                <div>
                    <div className="mb-2 font-medium">{t('billExport.overallProgress')}</div>
                    <Progress
                        percent={totalProgress}
                        status={
                            totalStatus === 'FAILED'
                                ? 'exception'
                                : totalStatus === 'SUCCESS'
                                  ? 'success'
                                  : 'active'
                        }
                    />
                </div>

                {totalStatus === 'SUCCESS' && (
                    <Alert
                        message={t('billExport.allSuccessTitle')}
                        description={t('billExport.allSuccessDesc')}
                        type="success"
                        showIcon
                    />
                )}

                {totalStatus === 'FAILED' && (
                    <Alert
                        message={t('billExport.partialFailTitle')}
                        description={t('billExport.partialFailDesc')}
                        type="error"
                        showIcon
                    />
                )}

                <div>
                    <div className="mb-2 font-medium">{t('billExport.fileDetails')}</div>
                    {progresses.length === 0 ? (
                        <div className="text-center text-gray-500 py-4">
                            {t('billExport.queryingProgress', { count: taskIds.length })}
                        </div>
                    ) : (
                        <Space direction="vertical" style={{ width: '100%' }} size="small">
                            {progresses.map((task) => (
                                <Card key={task.taskId} size="small" className="shadow-sm">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center flex-1">
                                            <div className="mr-3">{getStatusIcon(task.status)}</div>
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <Tag color="blue">{getFileTypeLabel(task.fileType)}</Tag>
                                                    <span className="text-sm">{getTaskStatusText(task)}</span>
                                                </div>
                                                <Progress
                                                    percent={task.progress}
                                                    size="small"
                                                    status={
                                                        task.status === 'FAILED'
                                                            ? 'exception'
                                                            : task.status === 'SUCCESS'
                                                              ? 'success'
                                                              : 'active'
                                                    }
                                                    showInfo={false}
                                                />
                                                {task.status === 'FAILED' && task.errorMessage && (
                                                    <div className="text-xs text-red-500 mt-1">
                                                        {task.errorMessage}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        {task.status === 'SUCCESS' && task.fileName && (
                                            <Button
                                                type="link"
                                                size="small"
                                                icon={<DownloadOutlined />}
                                                onClick={() => handleDownload(task.taskId)}
                                            >
                                                {t('billExport.download')}
                                            </Button>
                                        )}
                                    </div>
                                </Card>
                            ))}
                        </Space>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default BillExportProgressModal;
